import React, { useState, useEffect } from 'react';
import { getCommunityPoolStats, getOccultBankStats, forceOccultDistribution } from '../services/api';
import './AdminOccultBank.css';

const AdminOccultBank = ({ standalone = true }) => {
  const [communityStats, setCommunityStats] = useState(null);
  const [occultStats, setOccultStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchStats();
  }, [selectedMonth, selectedYear]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both community pool stats and occult bank stats
      const [communityResponse, occultResponse] = await Promise.all([
        getCommunityPoolStats(),
        getOccultBankStats()
      ]);
      
      setCommunityStats(communityResponse);
      setOccultStats(occultResponse);
    } catch (err) {
      console.error('Error fetching occult bank stats:', err);
      setError(err.response?.data?.message || 'Failed to fetch occult bank statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return parseFloat(num).toFixed(3);
  };

  const getCurrentMonthStats = () => {
    if (!communityStats || !communityStats.monthlyStats) return null;
    
    const targetMonth = selectedMonth;
    const targetYear = selectedYear;
    
    // Calculate stats for the selected month
    return {
      monthlyRevenue: communityStats.statistics?.totalPaymentsProcessed * 0.5 || 0,
      monthlyTasks: communityStats.statistics?.totalTasks || 0,
      monthlyAttempts: communityStats.statistics?.totalAttempts || 0,
      monthlyCompletions: communityStats.statistics?.totalCompletions || 0,
      month: new Date(targetYear, targetMonth).toLocaleString('default', { month: 'long', year: 'numeric' })
    };
  };

  const handleForceDistribution = async () => {
    try {
      const result = await forceOccultDistribution();
      if (result.success) {
        alert('Funds distributed successfully!');
        fetchStats(); // Refresh data
      } else {
        alert('Failed to distribute funds: ' + result.error);
      }
    } catch (error) {
      console.error('Error forcing distribution:', error);
      alert('Error distributing funds');
    }
  };

  const getMonthOptions = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(selectedYear, i);
      months.push({
        value: i,
        label: date.toLocaleString('default', { month: 'long' }),
        disabled: date > currentDate
      });
    }
    return months;
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2024; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  const monthlyStats = getCurrentMonthStats();

  if (loading) {
    return (
      <div className={`admin-occult-bank ${standalone ? 'standalone' : ''}`}>
        <div className="admin-loading">Loading occult bank statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`admin-occult-bank ${standalone ? 'standalone' : ''}`}>
        <div className="admin-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchStats} className="admin-retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-occult-bank ${standalone ? 'standalone' : ''}`}>
      {standalone && <h1 className="admin-title">Occult Bank Administration</h1>}
      
      {/* Month/Year Selection */}
      <div className="month-selector">
        <label>
          Month:
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {getMonthOptions().map(month => (
              <option key={month.value} value={month.value} disabled={month.disabled}>
                {month.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year:
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {getYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="occult-bank-overview">
        {/* Community Pool Section */}
        <div className="bank-balance-card">
          <h2>💰 Community Pool Balance</h2>
          <div className="bank-balance">
            {formatNumber(communityStats?.communityPool || 0)} WR Coins
          </div>
          <p className="bank-description">
            Accumulated from community task attempt fees (0.5 WR per attempt)
          </p>
        </div>

        {/* Occult Bank Section */}
        <div className="occult-balance-card">
          <h2>🏦 Occult Bank Balance</h2>
          <div className="occult-balance">
            {formatNumber(occultStats?.totalBalance || 0)} WR Coins
          </div>
          <p className="occult-description">
            Total taxes collected (20% tax) - Distributed to ATMs, Treasures, Casinos, Banks
          </p>
        </div>

        {/* Tax Collection by Category */}
        {occultStats?.taxesByCategory && (
          <div className="tax-category-section">
            <h2>📊 Tax Collection by Category</h2>
            <div className="tax-category-grid">
              <div className="tax-category-card">
                <h3>⛏️ Mining Operations</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.mining)} WR</div>
                <p>Taxes from mining coin collection</p>
              </div>
              
              <div className="tax-category-card">
                <h3>🖥️ Computer Parts</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.computerParts)} WR</div>
                <p>Taxes from part purchases</p>
              </div>
              
              <div className="tax-category-card">
                <h3>📝 Community Tasks</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.communityTasks)} WR</div>
                <p>Taxes from task creation fees</p>
              </div>
              
              <div className="tax-category-card">
                <h3>🏴‍☠️ Treasure Hunt</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.treasureHunt)} WR</div>
                <p>Taxes from treasure hunt activities</p>
              </div>
              
              <div className="tax-category-card">
                <h3>🌐 Network Operations</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.networkOperations)} WR</div>
                <p>Taxes from network-based activities</p>
              </div>
              
              <div className="tax-category-card">
                <h3>🎰 Slot Machines</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.slots)} WR</div>
                <p>Taxes from casino slot machine winnings</p>
              </div>
              
              <div className="tax-category-card">
                <h3>⚖️ Lawyer Fees</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.lawyerFees)} WR</div>
                <p>Full lawyer fees paid to bank (300 WR/hour)</p>
              </div>
              
              <div className="tax-category-card">
                <h3>🤝 P2P Hacks</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.p2p)} WR</div>
                <p>Taxes from user-to-user hack transactions</p>
              </div>
              
              <div className="tax-category-card">
                <h3>🏠 Mortgage Interest</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.mortgageInterest || 0)} WR</div>
                <p>Interest payments from mortgage loans</p>
              </div>
              
              <div className="tax-category-card">
                <h3>💸 Mortgage Penalties</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.mortgagePenalties || 0)} WR</div>
                <p>Early payoff penalties (10% of original loan)</p>
              </div>
              
              <div className="tax-category-card">
                <h3>💱 Transfer Fees</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.transfers || 0)} WR</div>
                <p>Fees from user-to-user transfers (2% of amount)</p>
              </div>
              
              <div className="tax-category-card">
                <h3>❓ Other</h3>
                <div className="tax-amount">{formatNumber(occultStats.taxesByCategory.other)} WR</div>
                <p>Miscellaneous tax collections</p>
              </div>
            </div>
          </div>
        )}

        {/* Mortgage Revenue Summary */}
        {occultStats?.taxesByCategory && (
          <div className="mortgage-revenue-section">
            <h2>🏠 Mortgage Revenue Summary</h2>
            <div className="mortgage-revenue-grid">
              <div className="mortgage-revenue-card">
                <h3>💰 Total Mortgage Revenue</h3>
                <div className="revenue-amount">
                  {formatNumber((occultStats.taxesByCategory.mortgageInterest || 0) + (occultStats.taxesByCategory.mortgagePenalties || 0))} WR
                </div>
                <p>Combined interest payments and early payoff penalties</p>
              </div>
              
              <div className="mortgage-revenue-card">
                <h3>📊 Interest vs Penalties</h3>
                <div className="revenue-breakdown">
                  <div className="breakdown-item">
                    <span className="breakdown-label">Interest Payments:</span>
                    <span className="breakdown-value">{formatNumber(occultStats.taxesByCategory.mortgageInterest || 0)} WR</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">Early Payoff Penalties:</span>
                    <span className="breakdown-value">{formatNumber(occultStats.taxesByCategory.mortgagePenalties || 0)} WR</span>
                  </div>
                </div>
              </div>
              
              <div className="mortgage-revenue-card">
                <h3>📈 Revenue Percentage</h3>
                <div className="revenue-percentage">
                  {occultStats.totalBalance > 0 
                    ? (((occultStats.taxesByCategory.mortgageInterest || 0) + (occultStats.taxesByCategory.mortgagePenalties || 0)) / occultStats.totalBalance * 100).toFixed(2)
                    : '0.00'
                  }%
                </div>
                <p>Mortgage revenue as % of total occult bank balance</p>
              </div>
            </div>
            
            <div className="mortgage-info-section">
              <div className="mortgage-info-card">
                <h4>💡 How Mortgage Revenue Works</h4>
                <ul>
                  <li><strong>Interest Payments:</strong> When users make monthly mortgage payments, the interest portion goes directly to the Occult Bank</li>
                  <li><strong>Early Payoff Penalties:</strong> Users who pay off mortgages early pay a 10% penalty of the original loan amount</li>
                  <li><strong>Administrative Fees:</strong> All mortgage-related fees support the White Rabbit banking infrastructure</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Fund Distribution Status */}
        {occultStats?.funding && (
          <div className="funding-status-section">
            <h2>💼 Fund Distribution Status</h2>
            <div className="funding-status-grid">
              <div className="funding-card">
                <h3>🏧 ATMs</h3>
                <div className="funding-amount">{formatNumber(occultStats.funding.atm.current)} / {formatNumber(occultStats.funding.atm.limit)} WR</div>
                <div className="funding-percentage">{occultStats.funding.atm.percentage}% allocation</div>
              </div>
              
              <div className="funding-card">
                <h3>📦 Treasures</h3>
                <div className="funding-amount">{formatNumber(occultStats.funding.treasure.current)} / {formatNumber(occultStats.funding.treasure.limit)} WR</div>
                <div className="funding-percentage">{occultStats.funding.treasure.percentage}% allocation</div>
              </div>
              
              <div className="funding-card">
                <h3>🎰 Casinos</h3>
                <div className="funding-amount">{formatNumber(occultStats.funding.casino.current)} / {formatNumber(occultStats.funding.casino.limit)} WR</div>
                <div className="funding-percentage">{occultStats.funding.casino.percentage}% allocation</div>
              </div>
              
              <div className="funding-card">
                <h3>🏛️ Banks</h3>
                <div className="funding-amount">{formatNumber(occultStats.funding.bank.current)} / {formatNumber(occultStats.funding.bank.limit)} WR</div>
                <div className="funding-percentage">{occultStats.funding.bank.percentage}% allocation</div>
              </div>
            </div>
          </div>
        )}

        {/* Monthly Stats Display */}
        {monthlyStats && (
          <div className="monthly-stats-section">
            <h2>📊 Monthly Statistics - {monthlyStats.month}</h2>
            <div className="monthly-stats-grid">
              <div className="monthly-stat-card">
                <h3>💰 Monthly Revenue</h3>
                <div className="stat-value">{formatNumber(monthlyStats.monthlyRevenue)} WR</div>
                <p>Revenue generated this month</p>
              </div>
              
              <div className="monthly-stat-card">
                <h3>📝 Tasks Created</h3>
                <div className="stat-value">{monthlyStats.monthlyTasks}</div>
                <p>New tasks added this month</p>
              </div>
              
              <div className="monthly-stat-card">
                <h3>🎯 Attempts Made</h3>
                <div className="stat-value">{monthlyStats.monthlyAttempts}</div>
                <p>Task attempts this month</p>
              </div>
              
              <div className="monthly-stat-card">
                <h3>✅ Completions</h3>
                <div className="stat-value">{monthlyStats.monthlyCompletions}</div>
                <p>Successful completions this month</p>
              </div>
            </div>
          </div>
        )}

        <div className="bank-stats-grid">
          <div className="stat-card">
            <h3>📝 Total Tasks</h3>
            <div className="stat-value">{communityStats?.statistics?.totalTasks || 0}</div>
          </div>
          
          <div className="stat-card">
            <h3>🎯 Total Attempts</h3>
            <div className="stat-value">{communityStats?.statistics?.totalAttempts || 0}</div>
          </div>
          
          <div className="stat-card">
            <h3>✅ Total Completions</h3>
            <div className="stat-value">{communityStats?.statistics?.totalCompletions || 0}</div>
          </div>
          
          <div className="stat-card">
            <h3>💳 Payments Processed</h3>
            <div className="stat-value">{communityStats?.statistics?.totalPaymentsProcessed || 0}</div>
          </div>
        </div>

        {communityStats?.statistics?.totalAttempts > 0 && (
          <div className="success-rate-card">
            <h3>📊 Overall Success Rate</h3>
            <div className="success-rate">
              {Math.round((communityStats.statistics.totalCompletions / communityStats.statistics.totalAttempts) * 100)}%
            </div>
          </div>
        )}
      </div>

      {communityStats?.statistics?.topCreators && communityStats.statistics.topCreators.length > 0 && (
        <div className="top-creators-section">
          <h2>🏆 Top Earning Creators</h2>
          <div className="creators-table">
            <table>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Creator</th>
                  <th>Tasks Created</th>
                  <th>Total Earnings</th>
                </tr>
              </thead>
              <tbody>
                {communityStats.statistics.topCreators.map((creator, index) => (
                  <tr key={creator._id}>
                    <td className="rank">#{index + 1}</td>
                    <td className="username">{creator.username}</td>
                    <td className="tasks-count">{creator.tasksCreated}</td>
                    <td className="earnings">{formatNumber(creator.earnings)} WR</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bank-info-section">
        <h2>ℹ️ How the Occult Bank Works</h2>
        <div className="info-grid">
          <div className="info-card">
            <h4>Payment Distribution</h4>
            <p>When a user pays 1 WR Coin to attempt a task:</p>
            <ul>
              <li>0.5 WR Coins go to the task creator</li>
              <li>0.5 WR Coins go to the occult bank</li>
            </ul>
          </div>
          
          <div className="info-card">
            <h4>Creator Benefits</h4>
            <p>Task creators earn from every paid attempt on their tasks, encouraging high-quality content creation.</p>
          </div>
          
          <div className="info-card">
            <h4>Occult Bank Fund</h4>
            <p>The occult bank accumulates funds for platform improvements, competitions, and community initiatives.</p>
          </div>
        </div>
      </div>

      <div className="admin-actions">
        <button onClick={fetchStats} className="refresh-button">
          🔄 Refresh Stats
        </button>
        <button onClick={handleForceDistribution} className="distribute-button">
          💸 Force Distribution
        </button>
      </div>
    </div>
  );
};

export default AdminOccultBank;